import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateLeft, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

import useViewport from 'Hooks/useViewport';
import ReviewScoreLogo from 'Features/Product/components/Reviews/ReviewScoreLogo';

import 'Features/Product/components/ShowcaseCarousel/ShowcaseCarousel.scss';

export default function ShowcaseCarousel({
  children,
  slideWidths,
  title,
  appearsOn,
  metacriticData,
}) {
  const [slideWidth, setSlideWidth] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [activeGameNumber, setActiveGameNumber] = useState(0);

  const sliderRef = useRef(null);
  const slidesRef = useRef([]);

  const {
    width,
    isAboveXs,
    isAboveSm,
    isAboveMd,
    isAboveLg,
    isAboveXl,
    currentBreakpoint,
  } = useViewport();

  const siteContainerWidths = {
    xs: 343,
    sm: 524,
    md: 704,
    lg: 944,
    xl: 1124,
    xxl: 1324,
  };

  // These are hardcoded for consistency and shouldn't be changed.
  let sliderGap = 16; // pixels (1rem)
  if (isAboveMd) sliderGap = 24; // pixels (1.5rem)
  if (isAboveXl) sliderGap = 32; // pixels (2rem)

  let carouselLeftMargin = (32) / 2;
  if (isAboveXs) carouselLeftMargin = (width - 524) / 2;
  if (isAboveSm) carouselLeftMargin = (width - 704) / 2;
  if (isAboveMd) carouselLeftMargin = (width - 944) / 2;
  if (isAboveLg) carouselLeftMargin = (width - 1124) / 2;
  if (isAboveXl) carouselLeftMargin = (width - 1324) / 2;

  // const elementIsVisibleInViewport = () => {
  //   const slidesInViewport = [];

  //   slidesRef.current.forEach((el, i) => {
  //     if (!el) return false;

  //     const { top, left, bottom, right } = el && el.getBoundingClientRect();
  //     const { innerHeight, innerWidth } = window;

  //     // if (!partiallyVisible) {
  //     //   return ((top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth));
  //     // }

  //     if (((top > 0 && top < innerHeight) || (bottom > 0 && bottom < innerHeight)) && ((left > 0 && left < innerWidth) || (right > 0 && right < innerWidth))) { // eslint-disable-line max-len
  //       slidesInViewport.push(i);
  //     }
  //     return false;
  //   });

  //   return setVisibleSlides(slidesInViewport);
  // };

  // Set current slide width so it can be used to move arousel left and right when using arrows
  useEffect(() => {
    setSlideWidth(slideWidths[currentBreakpoint]);
  }, [slideWidths, currentBreakpoint]);

  const loadNext = () => {
    setSliderPosition(sliderPosition - (slideWidth + sliderGap));
    setActiveGameNumber(activeGameNumber + 1);
  };

  const loadFirst = () => {
    setSliderPosition(0);
    setActiveGameNumber(0);
  };

  const loadPrevious = () => {
    setSliderPosition(sliderPosition + (slideWidth + sliderGap));
    setActiveGameNumber(activeGameNumber - 1);
  };

  let scrollable = true;
  let centered = false;

  // Width of entire track of slides
  const sliderContentWidth = (children.length * slideWidth) + ((children.length - 1) * sliderGap);
  // If the track is less than the width of the site, it needs to display centered
  if (sliderContentWidth <= siteContainerWidths[currentBreakpoint]) {
    centered = true;
    scrollable = false;
  }

  return (
    <>
      <div className="ShowcaseSection__headingContainer container">
        {appearsOn !== 'critic' &&
          <h2 className={classNames('ShowcaseHeading', { 'ShowcaseHeading--centered': centered && appearsOn === 'editions' })}>
            {title}
          </h2>
        }
        {appearsOn === 'critic' &&
          <div className="ShowcaseSection__headingContainer__critic">
            <h2 className="ShowcaseHeading">
              {title}
            </h2>
            <ReviewScoreLogo
              reviewCount={metacriticData.reviewCount}
              logo="metaCritic"
              score={metacriticData.score}
            />
          </div>
        }
        {scrollable && isAboveMd &&
          <div className="ShowcaseSection__headingContainer__buttons">
            <button
              className={classNames('ShowcaseButton ShowcaseButton--circle', { 'ShowcaseButton--hidden': activeGameNumber + 1 !== children.length })}
              disabled={activeGameNumber === 0}
              onClick={() => loadFirst()}
            >
              <FontAwesomeIcon icon={faRotateLeft} title="Reset carousel" />
            </button>

            <button
              className="ShowcaseButton ShowcaseButton--circle"
              disabled={activeGameNumber === 0}
              onClick={() => loadPrevious()}
            >
              <FontAwesomeIcon icon={faChevronLeft} title="Previous" />
            </button>

            <button
              className="ShowcaseButton ShowcaseButton--circle"
              disabled={activeGameNumber + 1 === children.length}
              onClick={() => loadNext()}
            >
              <FontAwesomeIcon icon={faChevronRight} title="Next" />
            </button>
          </div>
        }
      </div>

      <div className={classNames('ShowcaseCarousel', { 'ShowcaseCarousel--scroll': scrollable }, { 'ShowcaseCarousel--centered': centered })}>
        <ul className="ShowcaseCarousel__slides" ref={sliderRef} style={{ left: `${sliderPosition}px` }}>
          {React.Children.map(children, (child, index) => (
            <li
              className={`ShowcaseCarousel__slides__slide ShowcaseCarousel__slides__slide__${index}`}
              style={{
                width: slideWidth,
                marginLeft: scrollable && index === 0 ? carouselLeftMargin : 0,
              }}
              ref={(element) => { slidesRef.current[index] = element; }}
              key={`slide_${index + 1}`}
              // aria-hidden={!includes(visibleSlides, index)}
              tabIndex={-1}
            >
              {React.cloneElement(child)}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

ShowcaseCarousel.propTypes = {
  slideWidths: PropTypes.shape({}),
  title: PropTypes.string,
  appearsOn: PropTypes.string,
  metacriticData: PropTypes.shape({}),
};

ShowcaseCarousel.defaultProps = {
  slideWidths: {},
  title: '',
  appearsOn: '',
  metacriticData: {},
};
